import { AxiosResponse } from 'axios';

import { normalizeBaseGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IBaseGist, IPageType } from 'utils/types';

import { analyticsInstance } from '../base';

import {
  normalizeCalculatedGraph,
  normalizeDashboard,
  normalizeDashboardFolder,
  normalizeDashboardGraphs,
  normalizeDashboardList,
  normalizeGraph,
} from './normalizers';
import {
  ICalculatedGraph,
  ICalculatedGraphPayload,
  IDashboard,
  IDashboardFolder,
  IDashboardFolderFormData,
  IDashboardFormData,
  IDashboardGraphs,
  IDashboardList,
  IGraph,
  IGraphFormData,
  TDashboardFolderData,
} from './types';

const dashboardsAPI = {
  async getAllDashboardFolders(
    filters: IAllPageFilters,
  ): Promise<IPageType<IDashboardFolder>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await analyticsInstance.get('dashboard-folders', {
      params: normalizedFilter,
    });

    return normalizePageData<TDashboardFolderData, IDashboardFolder>(
      response.data,
      normalizeDashboardFolder,
      JSON.stringify(filters?.search),
    );
  },

  async getDashboardFolder(id: string): Promise<IDashboardFolder> {
    const response = await analyticsInstance.get(`dashboard-folders/${id}`);
    return normalizeDashboardFolder(response.data);
  },

  async getDashboards(id: number): Promise<IDashboardList> {
    const response = await analyticsInstance.get(`dashboard-folders/${id}/dashboards`);
    return normalizeDashboardList(response.data);
  },

  async createDashboardFolder({ payload }: IDashboardFolderFormData): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.post(`dashboard-folders`, normalizedPayload);

    return normalizeBaseGist(response?.data);
  },

  async editDashboardFolder({
    id,
    payload,
  }: IDashboardFolderFormData): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.put(
      `dashboard-folders/${id}`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async copyDashboardFolder({
    id,
    payload,
  }: IDashboardFolderFormData): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.post(
      `dashboard-folders/${id}/copy`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async deleteDashboardFolder(id: number | string): Promise<AxiosResponse> {
    const result = await analyticsInstance.delete(`dashboard-folders/${id}`);

    return result;
  },

  async getDashboardGraphs(id: string): Promise<IDashboardGraphs> {
    const response = await analyticsInstance.get(`dashboards/${id}/graphs`);
    return normalizeDashboardGraphs(response.data);
  },

  async getDashboard(id: string): Promise<IDashboard> {
    const response = await analyticsInstance.get(`dashboards/${id}`);
    return normalizeDashboard(response.data);
  },

  async createDashboard({ payload }: IDashboardFormData): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.post(`dashboards`, normalizedPayload);

    return normalizeBaseGist(response?.data);
  },

  async editDashboard({ id, payload }: IDashboardFormData): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.put(`dashboards/${id}`, normalizedPayload);

    return normalizeBaseGist(response?.data);
  },

  async deleteDashboard(id: number | string): Promise<AxiosResponse> {
    const result = await analyticsInstance.delete(`dashboards/${id}`);

    return result;
  },

  async getGraph(id: string): Promise<IGraph> {
    const response = await analyticsInstance.get(`graphs/${id}`);
    return normalizeGraph(response.data);
  },

  async createGraph({ payload }: IGraphFormData): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.post(`graphs`, normalizedPayload);

    return normalizeBaseGist(response?.data);
  },

  async editGraph({ id, payload }: IGraphFormData): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.put(`graphs/${id}`, normalizedPayload);

    return normalizeBaseGist(response?.data);
  },

  async calculateGraph({
    id,
    payload,
  }: ICalculatedGraphPayload): Promise<ICalculatedGraph> {
    const normalizedPayload = prepareFormData(payload);

    const response = await analyticsInstance.post(
      `graphs/${id}/collect`,
      normalizedPayload,
    );

    return normalizeCalculatedGraph(response.data.result);
  },

  async deleteGraph(id: number | string): Promise<AxiosResponse> {
    const result = await analyticsInstance.delete(`graphs/${id}`);

    return result;
  },
};

export default dashboardsAPI;
